/**
 * My schematics: imported schematic files, kept in this browser only (IndexedDB), for all worlds.
 *
 * The files are never uploaded or shared by MC Planner — builds belong to their creators. The shared
 * plan only knows each file's hash, size and source link, so a friend gets the file from the original
 * post and imports it themselves (the same file → the same hash → the building shows up for them too).
 */
import type { SchematicFormat } from "@mcp/world";
import { idb } from "./idb.ts";

export interface SchematicEntry {
  /** sha256 of the file (hex) */
  hash: string;
  /** shown name (from the file's metadata or file name; editable) */
  name: string;
  fileName: string;
  format: SchematicFormat;
  version: number;
  dataVersion: number | null;
  /** x · y · z */
  size: [number, number, number];
  /** non-air blocks */
  blocks: number;
  author?: string;
  description?: string;
  /** where it's from (link to the original post) */
  sourceUrl?: string;
  /** Litematica preview picture as a data: URL */
  preview?: string;
  /** item id → count, largest first */
  materials: [string, number][];
  bytes: number;
  addedAt: number;
  /** last time it was placed (the Schematics view sorts by it) */
  usedAt?: number;
  /** 3D thumbnail rendered here with the person's own textures (data: URL, never leaves this browser) */
  thumb?: string;
  /** what the thumbnail was rendered with (textures label + lod): a new jar renders it again */
  thumbKey?: string;
}

/** Largest file we import. */
export const MAX_SCHEMATIC_BYTES = 60 * 1024 * 1024;

const INDEX = "schematic-index";
const fileKey = (hash: string) => `schematic:${hash}`;

export async function sha256Hex(blob: Blob): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", await blob.arrayBuffer()));
  return Array.from(d, (b) => b.toString(16).padStart(2, "0")).join("");
}

export async function listSchematics(): Promise<SchematicEntry[]> {
  return (await idb.get<SchematicEntry[]>(INDEX)) ?? [];
}

export async function getSchematicFile(hash: string): Promise<Blob | null> {
  return (await idb.get<Blob>(fileKey(hash))) ?? null;
}

/** index writes one after the other (a thumbnail and a rename at the same moment must not undo each other) */
let writes: Promise<unknown> = Promise.resolve();
function queued<T>(job: () => Promise<T>): Promise<T> {
  const p = writes.then(job, job);
  writes = p.catch(() => undefined);
  return p;
}

/** Add (or replace) an entry and its file. Newest first. */
export function saveSchematic(entry: SchematicEntry, file: Blob): Promise<SchematicEntry[]> {
  return queued(async () => {
    await idb.set(fileKey(entry.hash), file);
    const list = [entry, ...(await listSchematics()).filter((e) => e.hash !== entry.hash)];
    await idb.set(INDEX, list);
    return list;
  });
}

export function updateSchematic(hash: string, patch: Partial<SchematicEntry>): Promise<SchematicEntry[]> {
  return queued(async () => {
    const list = (await listSchematics()).map((e) => (e.hash === hash ? { ...e, ...patch, hash } : e));
    await idb.set(INDEX, list);
    return list;
  });
}

export function removeSchematic(hash: string): Promise<SchematicEntry[]> {
  return queued(async () => {
    const list = (await listSchematics()).filter((e) => e.hash !== hash);
    await idb.set(INDEX, list);
    await idb.del(fileKey(hash));
    return list;
  });
}

/** RGBA preview → PNG data URL (scaled up with crisp pixels if tiny). */
export function previewDataUrl(p: { w: number; h: number; rgba: Uint8Array }): string | undefined {
  try {
    const src = document.createElement("canvas");
    src.width = p.w;
    src.height = p.h;
    src.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(p.rgba), p.w, p.h), 0, 0);
    if (p.w >= 96) return src.toDataURL("image/png");
    const k = Math.ceil(96 / p.w);
    const c = document.createElement("canvas");
    c.width = p.w * k;
    c.height = p.h * k;
    const g = c.getContext("2d")!;
    g.imageSmoothingEnabled = false;
    g.drawImage(src, 0, 0, c.width, c.height);
    return c.toDataURL("image/png");
  } catch {
    return undefined;
  }
}

/** default name from the file name: "medieval_house.litematic" → "medieval house" */
export function nameFromFile(fileName: string): string {
  return fileName.replace(/\.(litematic|schem|schematic|nbt)$/i, "").replace(/[_]+/g, " ").trim() || "Schematic";
}

/** ".litematic" etc. from a file name (lower case), "" if it has none of the four */
export function schematicExt(fileName: string): string {
  const m = /\.(litematic|schem|schematic|nbt)$/i.exec(fileName);
  return m ? `.${m[1].toLowerCase()}` : "";
}

/** 38 KB · 1.4 MB */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1048576).toFixed(bytes < 10 * 1048576 ? 1 : 0)} MB`;
}

/** 144 → "2 st + 16" (stacks of 64), "" under one stack */
export function stacks(n: number): string {
  if (n < 64) return "";
  const s = Math.floor(n / 64), r = n % 64;
  return r ? `${s} st + ${r}` : `${s} st`;
}

/** "just now" · "5 minutes ago" · "3 days ago" · "12 Mar 2026" */
export function timeAgo(t: number, now = Date.now()): string {
  const s = Math.max(0, (now - t) / 1000);
  const n = (v: number, unit: string) => `${v} ${unit}${v === 1 ? "" : "s"} ago`;
  if (s < 60) return "just now";
  if (s < 3600) return n(Math.floor(s / 60), "minute");
  if (s < 86400) return n(Math.floor(s / 3600), "hour");
  if (s < 86400 * 30) return n(Math.floor(s / 86400), "day");
  return new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
